<template>
  <section>
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card" style="width: 1000px; min-height: 400px">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ $t('attributes.modify') + $t('attributes.permission') }}
          </p>
        </header>

        <section class="modal-card-body">
          <div
            v-for="main in systems"
            :key="main.id"
            class="columns main-system-block"
          >
            <div class="column is-3">
              <b-checkbox
                v-model="main.enabled"
                @input="onSelectMainSystem(main)"
              >
                <p :class="{ 'has-text-grey-light': !main.status }">
                  {{ main.title }}
                </p>
              </b-checkbox>
            </div>
            <div class="column is-9">
              <div class="columns is-multiline">
                <div
                  v-for="sub in main.sub_systems"
                  :key="sub.id"
                  class="column is-3"
                >
                  <b-checkbox
                    v-model="permissions"
                    :native-value="sub.id"
                    @input="onSelectSubSystem(sub)"
                  >
                    <p
                      :class="{
                        'has-text-grey-light': !main.status || !sub.status
                      }"
                    >
                      {{ sub.title }}
                    </p>
                  </b-checkbox>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="isLoading"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="isLoading"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ $t('attributes.update') }}
          </b-button>
        </footer>

        <b-loading :active.sync="isLoading" :can-cancel="false"></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { PERMISSION_GROUPS_API, SYSTEM_MAIN_SYSTEM_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'GroupPermissionsForm',

  props: {
    id: {
      type: Number,
      required: true
    },
    sending: {
      type: Boolean
    }
  },
  data() {
    return {
      loading: false,
      systems: [],
      permissionGroup: null,

      permissions: []
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.sending
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true

      await this.fetchSystems()
      await this.fetchPermissionGroup()

      this.permissions = []
      if (this.permissionGroup) {
        this.setupPermissionList()
      }
      this.setupSystems()

      this.loading = false
    },
    async fetchSystems() {
      try {
        const api = `${SYSTEM_MAIN_SYSTEM_API}`
        const res = await this.axios.get(api)
        this.systems = res.data.data
      } catch (e) {
        this.systems = []

        notifyService.fetch_error('main_system', e)
      }
    },
    async fetchPermissionGroup() {
      try {
        const api = `${PERMISSION_GROUPS_API}/${this.id}`
        const res = await this.axios.get(api)
        this.permissionGroup = res.data.data
      } catch (e) {
        this.permissionGroup = null

        notifyService.fetch_error('permission_group', e)
      }
    },
    setupPermissionList() {
      const permissionList = this.permissionGroup.main_systems
        .sort((a, b) => a.sort - b.sort)
        .reduce((permissions, main) => {
          let sub_systems = main.sub_systems || []

          sub_systems = sub_systems
            .sort((a, b) => a.sort - b.sort)
            .reduce((sub_permissions, sub) => {
              const sub_permission = {
                id: sub.id,
                main_systems_id: main.id,
                sort: sub.sort,
                status: sub.status === '1'
              }
              return [...sub_permissions, sub_permission]
            }, [])

          return [...permissions, ...sub_systems]
        }, [])

      this.permissions = permissionList.map(p => p.id)
    },
    setupSystems() {
      this.systems = this.systems
        .sort((a, b) => a.sort - b.sort)
        .map(main => {
          let sub_systems = main.sub_systems || []

          sub_systems = sub_systems
            .sort((a, b) => a.sort - b.sort)
            .map(sub => {
              return {
                id: sub.id,
                main_systems_id: main.id,
                sort: sub.sort,
                title: sub.title,
                status: sub.status === '1'
              }
            })

          const enabled =
            sub_systems.length > 0 &&
            sub_systems.every(
              sub => this.permissions.findIndex(p => p === sub.id) !== -1
            )

          return {
            id: main.id,
            sort: main.sort,
            title: main.title,
            status: main.status === '1',
            sub_systems: sub_systems,
            enabled: enabled
          }
        })
    },
    onSelectMainSystem(main) {
      const sub_systems = main.sub_systems || []
      const permissions = sub_systems
        .sort((a, b) => a.sort - b.sort)
        .map(sub => sub.id)

      if (main.enabled) {
        this.permissions = [...new Set([...this.permissions, ...permissions])]
      } else {
        this.permissions = this.permissions.filter(
          p => permissions.findIndex(m => m === p) === -1
        )
      }
    },
    onSelectSubSystem(sub) {
      const main = this.systems.find(main => main.id === sub.main_systems_id)
      main.enabled =
        main.sub_systems.length > 0 &&
        main.sub_systems.every(
          sub => this.permissions.findIndex(p => p === sub.id) !== -1
        )
    },
    async onSend() {
      if (!this.permissionGroup) {
        return
      }

      const permissions = this.permissions.sort((a, b) => a - b)

      this.$emit('send', this.id, { permissions })
    }
  }
}
</script>

<style lang="scss" scoped>
.main-system-block {
  + .main-system-block {
    border-top: solid lightgrey 1px;
  }
}

.b-checkbox {
  &.checkbox {
    align-items: initial;
  }
}
</style>
